import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getEmployee, getFieldFlags, getCandidateSubmission } from '../services/employeeService';
import { getViewPresignedUrl } from '../services/fileService';
import { EmployeeRecord, FieldFlag } from '../types';
import { FiArrowLeft, FiFileText, FiDownload, FiAlertCircle, FiX, FiEdit } from 'react-icons/fi';
import toast from 'react-hot-toast';

const CandidateDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<EmployeeRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldFlags, setFieldFlags] = useState<FieldFlag[]>([]);

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    setIsLoading(true);
    try {
      let employeeData;
      
      if (id) {
        // HR/Admin viewing specific candidate's application
        employeeData = await getEmployee(id);
        
        // Load field flags only for HR/Admin users (not candidates)
        try {
          const flagsData = await getFieldFlags(employeeData.id);
          setFieldFlags(flagsData);
          console.log('Loaded field flags from database:', flagsData);
        } catch (error: any) {
          // If 403 (Forbidden), user is likely a candidate - ignore the error
          if (error.response?.status === 403) {
            console.log('User does not have permission to view field flags (likely a candidate)');
            setFieldFlags([]);
          } else {
            throw error;
          }
        }
      } else {
        // Candidate viewing their own application
        const response = await getCandidateSubmission();
        employeeData = response.data;
        // Candidates cannot see field flags, so don't try to load them
        setFieldFlags([]);
      }
      
      setEmployee(employeeData);
    } catch (error) {
      console.error('Failed to fetch employee:', error);
      toast.error('Failed to load application data');
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldFlag = (fieldName: string): FieldFlag | undefined => {
    // Ensure fieldFlags is an array
    if (!Array.isArray(fieldFlags)) {
      console.warn('fieldFlags is not an array:', fieldFlags);
      return undefined;
    }
    
    // Check if the specific field is flagged
    let flag = fieldFlags.find(flag => flag.fieldPath === fieldName);
    if (flag) return flag;
    
    // Check if the section containing this field is flagged
    const fieldToSectionMap: Record<string, string> = {
      'fullNames': 'personalDetails',
      'surname': 'personalDetails',
      'email': 'personalDetails',
      'contactNumber': 'personalDetails',
      'dateOfBirth': 'personalDetails',
      'idNumber': 'personalDetails',
      'maritalStatus': 'personalDetails',
      'positionAppliedFor': 'personalDetails',

      'employmentStatus': 'employmentStatus',
      'currentEmployer': 'employmentStatus',
      'currentPosition': 'employmentStatus',
      'previouslyEmployedByTrigon': 'employmentStatus',
      'previousTrigonPosition': 'employmentStatus',
      'previousTrigonEmployeeNumber': 'employmentStatus',

      'residentialAddress': 'address',
    };
    
    const section = fieldToSectionMap[fieldName];
    return section ? fieldFlags.find(flag => flag.fieldPath === section) : undefined;
  };

  const isFieldFlagged = (fieldName: string) => {
    // Only show flags if status is requested_rework
    if (employee?.status !== 'requested_rework') {
      return false;
    }
    return !!getFieldFlag(fieldName);
  };

  const isSectionFlagged = (sectionName: string) => {
    // Only show flags if status is requested_rework
    if (employee?.status !== 'requested_rework') {
      return false;
    }
    // Ensure fieldFlags is an array
    if (!Array.isArray(fieldFlags)) {
      console.warn('fieldFlags is not an array:', fieldFlags);
      return false;
    }
    return fieldFlags.some(flag => flag.fieldPath === sectionName);
  };

  // Component for showing field flag message
  const FieldFlagIndicator: React.FC<{ fieldName: string }> = ({ fieldName }) => {
    const flag = getFieldFlag(fieldName);
    const [showMessage, setShowMessage] = useState(false);

    // Close popup when clicking outside
    useEffect(() => {
      if (!showMessage || !flag) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (!target.closest('.flag-popup-container')) {
          setShowMessage(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, [showMessage, flag]);

    // Only show flag indicator if status is requested_rework
    if (employee?.status !== 'requested_rework' || !flag) {
      return null;
    }

    if (!flag) return null;

    return (
      <div className="relative inline-block ml-2 flag-popup-container">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowMessage(!showMessage);
          }}
          className="text-orange-500 hover:text-orange-600 transition-colors"
          title="Click to see what needs to be fixed"
        >
          <FiAlertCircle size={16} />
        </button>
        
        {showMessage && (
          <div className="absolute left-0 top-6 z-10 w-64 p-3 bg-orange-50 border border-orange-200 rounded-lg shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800 mb-1">Attention Required:</p>
                <p className="text-sm text-orange-700">{flag.message}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMessage(false);
                }}
                className="text-orange-400 hover:text-orange-600 ml-2 flex-shrink-0"
              >
                <FiX size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleDownloadDocument = async (attachment: any) => {
    try {
      // Show loading state
      toast.loading('Preparing document for download...');
      
      // Get presigned URL
      const downloadUrl = await getViewPresignedUrl(attachment.fileKey);
      
      // Open in new tab
      window.open(downloadUrl, '_blank');
      
      toast.dismiss();
      toast.success('Document opened in new tab');
    } catch (error) {
      toast.dismiss();
      console.error('Failed to download document:', error);
      toast.error('Failed to open document');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new_submission':
        return 'bg-blue-100 text-blue-800';
      case 'candidate':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-purple-100 text-purple-800';
      case 'requested_rework':
        return 'bg-orange-100 text-orange-800';
      case 'profile_verified':
        return 'bg-teal-100 text-teal-800';
      case 'available_for_hire':
        return 'bg-indigo-100 text-indigo-800';
      case 'hired':
        return 'bg-emerald-100 text-emerald-800';
      case 'current_employee':
        return 'bg-green-100 text-green-800';
      case 'terminated':
        return 'bg-gray-100 text-gray-800';
      case 'blacklisted':
        return 'bg-black text-white';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <span className="sr-only">Loading application details</span>
        {/* Header skeleton mirroring the actual header layout */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center space-x-4">
            <div className="relative overflow-hidden rounded-lg bg-primary-300 h-10 w-10" aria-hidden="true">
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="relative overflow-hidden rounded bg-primary-300 h-6 w-1/3" aria-hidden="true">
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden" style={{ animationDelay: '100ms' }} />
              </div>
              <div className="relative overflow-hidden rounded bg-primary-300 h-3 w-1/4" aria-hidden="true">
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden" style={{ animationDelay: '200ms' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Body skeleton — three card-shaped sections */}
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white shadow rounded-lg p-6 space-y-4">
            <div className="relative overflow-hidden rounded bg-primary-300 h-5 w-1/4" aria-hidden="true">
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden" style={{ animationDelay: `${i * 120}ms` }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="space-y-2">
                  <div className="relative overflow-hidden rounded bg-primary-300 h-3 w-1/3" aria-hidden="true">
                    <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden" style={{ animationDelay: `${i * 120 + j * 60 + 100}ms` }} />
                  </div>
                  <div className="relative overflow-hidden rounded bg-primary-300 h-4 w-2/3" aria-hidden="true">
                    <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden" style={{ animationDelay: `${i * 120 + j * 60 + 200}ms` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Application not found.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiArrowLeft />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                My Application
              </h1>
              <p className="text-gray-600">Reference: {employee.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(employee.status)}`}>
              {employee.status.replace('_', ' ').toUpperCase()}
            </span>
            {employee.status === 'requested_rework' && !id && (
              <Link
                to="/resubmit"
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <FiEdit size={16} />
                Update Application
              </Link>
            )}
          </div>
        </div>
        
        {employee.status === 'requested_rework' && Array.isArray(fieldFlags) && fieldFlags.length > 0 && (
          <div className="mt-4 bg-orange-50 border-l-4 border-orange-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-orange-700">
                  <strong>Attention Required:</strong> Some fields in your application need updates. Look for the orange warning icons next to fields that need attention.
                </p>
                <p className="text-sm text-orange-600 mt-2">
                  Flagged fields: <strong>{fieldFlags.map(flag => 
                    flag.fieldPath.charAt(0).toUpperCase() + flag.fieldPath.slice(1).replace(/([A-Z])/g, ' $1')
                  ).join(', ')}</strong>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {/* Personal Details */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('personalDetails') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('personalDetails') ? 'text-orange-800' : ''}`}>
            Personal Details
            {isSectionFlagged('personalDetails') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Full Names *
                <FieldFlagIndicator fieldName="fullNames" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('fullNames') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.fullNames}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Surname *
                <FieldFlagIndicator fieldName="surname" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('surname') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.surname}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Email
                <FieldFlagIndicator fieldName="email" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('email') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.email || 'Not provided'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Contact Number *
                <FieldFlagIndicator fieldName="contactNumber" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('contactNumber') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.contactNumber}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Date of Birth *
                <FieldFlagIndicator fieldName="dateOfBirth" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('dateOfBirth') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {new Date(employee.dateOfBirth).toLocaleDateString()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                ID Number *
                <FieldFlagIndicator fieldName="idNumber" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('idNumber') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.idNumber}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Marital Status
                <FieldFlagIndicator fieldName="maritalStatus" />
              </label>
              <p className={`text-gray-900 p-2 rounded capitalize ${isFieldFlagged('maritalStatus') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.maritalStatus}
              </p>
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('employmentStatus') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('employmentStatus') ? 'text-orange-800' : ''}`}>
            Employment Information
            {isSectionFlagged('employmentStatus') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Position Applied For
                <FieldFlagIndicator fieldName="positionAppliedFor" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('positionAppliedFor') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {Array.isArray(employee.positionAppliedFor) ? employee.positionAppliedFor.join(', ') : employee.positionAppliedFor}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Employment Status
                <FieldFlagIndicator fieldName="employmentStatus" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('employmentStatus') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.employmentStatus}
              </p>
            </div>
            {employee.currentEmployer && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Current Employer
                  <FieldFlagIndicator fieldName="currentEmployer" />
                </label>
                <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('currentEmployer') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                  {employee.currentEmployer}
                </p>
              </div>
            )}
            {employee.currentPosition && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Current Position
                  <FieldFlagIndicator fieldName="currentPosition" />
                </label>
                <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('currentPosition') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                  {employee.currentPosition}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Previously Employed by Trigon Mining
                <FieldFlagIndicator fieldName="previouslyEmployedByTrigon" />
              </label>
              <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('previouslyEmployedByTrigon') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                {employee.previouslyEmployedByTrigon}
              </p>
            </div>
            {employee.previouslyEmployedByTrigon === 'Yes' && employee.previousTrigonPosition && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Previous Position at Trigon
                  <FieldFlagIndicator fieldName="previousTrigonPosition" />
                </label>
                <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('previousTrigonPosition') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                  {employee.previousTrigonPosition}
                </p>
              </div>
            )}
            {employee.previouslyEmployedByTrigon === 'Yes' && employee.previousTrigonEmployeeNumber && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Previous Employee Number
                  <FieldFlagIndicator fieldName="previousTrigonEmployeeNumber" />
                </label>
                <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('previousTrigonEmployeeNumber') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                  {employee.previousTrigonEmployeeNumber}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Residential Address */}
        {employee.residentialAddress && (
          <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('address') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
            <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('address') ? 'text-orange-800' : ''}`}>
              Residential Address
              {isSectionFlagged('address') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employee.residentialAddress.streetAddress && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Street Address and Number
                    <FieldFlagIndicator fieldName="residentialAddress.streetAddress" />
                  </label>
                  <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('residentialAddress.streetAddress') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                    {employee.residentialAddress.streetAddress}
                  </p>
                </div>
              )}
              {employee.residentialAddress.townCity && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Town/City
                    <FieldFlagIndicator fieldName="residentialAddress.townCity" />
                  </label>
                  <p className={`text-gray-900 p-2 rounded ${isFieldFlagged('residentialAddress.townCity') ? 'border border-orange-400 bg-orange-50' : ''}`}>
                    {employee.residentialAddress.townCity === 'Other' && employee.residentialAddress.customTownCity
                      ? employee.residentialAddress.customTownCity
                      : employee.residentialAddress.townCity}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dependents */}
        {employee.dependents && employee.dependents.length > 0 && (
          <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('dependents') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
            <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('dependents') ? 'text-orange-800' : ''}`}>
              Dependents
              {isSectionFlagged('dependents') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
            </h2>
            <div className="space-y-4">
              {employee.dependents.map((dependent, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Names</label>
                    <p className="text-gray-900 p-2 rounded">{dependent.fullNames}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Surname</label>
                    <p className="text-gray-900 p-2 rounded">{dependent.surname}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relation</label>
                    <p className="text-gray-900 p-2 rounded">{dependent.relation || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
                    <p className="text-gray-900 p-2 rounded">{dependent.idNumber || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Maiden Name</label>
                    <p className="text-gray-900 p-2 rounded">{dependent.maidenName || 'Not applicable'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bank Details */}
        {employee.bankingDetails && (
          <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('bankingDetails') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
            <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('bankingDetails') ? 'text-orange-800' : ''}`}>
              Bank Details
              {isSectionFlagged('bankingDetails') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                <p className="text-gray-900 p-2 rounded">{employee.bankingDetails.bank}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <p className="text-gray-900 p-2 rounded">{employee.bankingDetails.accountNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                <p className="text-gray-900 p-2 rounded capitalize">{employee.bankingDetails.accountType}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <p className="text-gray-900 p-2 rounded">{employee.bankingDetails.branch}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                <p className="text-gray-900 p-2 rounded">{employee.bankingDetails.accountName}</p>
              </div>
            </div>
          </div>
        )}



        {/* Documents */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Uploaded Documents</h2>
          <div className="space-y-3">
            {employee.attachments && employee.attachments.length > 0 ? (
              employee.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center space-x-2">
                    <FiFileText className="text-green-600" />
                    <div>
                      <span className="font-medium">{attachment.documentType}</span>
                      <p className="text-sm text-gray-600">{attachment.fileName}</p>
                    </div>
                    <FieldFlagIndicator fieldName={`attachments.${index}.${attachment.documentType}`} />
                  </div>
                  <button 
                    onClick={() => handleDownloadDocument(attachment)}
                    className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                    title="Download document"
                  >
                    <FiDownload />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">No documents uploaded</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetailsPage;
